import React from "react";
import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer";

/**
 * Standard 4×6" thermal shipping label stock, PDF in landscape: 6" wide × 4" tall
 * (432×288 pt). Fits common Zebra and similar printers.
 */
export const ZEBRA_SHIPPING_LABEL_LANDSCAPE_PT: [number, number] = [6 * 72, 4 * 72];

/** DD/MM/YYYY from ISO date/datetime; prefers YYYY-MM-DD prefix so timezone does not shift calendar day. */
export function formatEtaForShippingLabel(iso: string): string {
  const trimmed = iso.trim();
  const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const [, y, mo, d] = ymd;
    return `${d}/${mo}/${y}`;
  }
  const t = new Date(trimmed);
  if (Number.isNaN(t.getTime())) return trimmed;
  const dd = String(t.getDate()).padStart(2, "0");
  const mm = String(t.getMonth() + 1).padStart(2, "0");
  const yyyy = String(t.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    padding: 14,
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  /** Ref first so it never collides with the large date. */
  refTop: {
    width: "100%",
    flexDirection: "column",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#cccccc",
  },
  refLabel: {
    fontSize: 12,
    color: "#444",
    marginBottom: 4,
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  ref: {
    fontSize: 16,
    color: "#111",
    fontWeight: "bold",
    lineHeight: 1.25,
  },
  body: {
    flexGrow: 1,
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    paddingTop: 4,
  },
  heading: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 8,
    letterSpacing: 0.4,
  },
  dateValue: {
    fontSize: 70,
    fontWeight: "bold",
    lineHeight: 1.05,
  },
});

function CustomerEtaOverrideShippingLabelDocument({
  dateFormatted,
  reference,
}: {
  dateFormatted: string;
  reference: string;
}) {
  return (
    <Document>
      <Page size={ZEBRA_SHIPPING_LABEL_LANDSCAPE_PT} style={styles.page}>
        <View style={styles.refTop}>
          <Text style={styles.refLabel}>Ref</Text>
          <Text style={styles.ref}>{reference}</Text>
        </View>
        <View style={styles.body}>
          <Text style={styles.heading}>Afwijkende Afleverdatum:</Text>
          <Text style={styles.dateValue}>{dateFormatted}</Text>
        </View>
      </Page>
    </Document>
  );
}

/** Client-side PDF for 4×6" landscape shipping label (Zebra-style). */
export async function downloadCustomerEtaOverrideShippingLabelPdf(params: {
  bookingEtaIso: string;
  /** e.g. EFF5183SEA — use getBookingEffReference(booking). */
  bookingReference: string;
}): Promise<void> {
  const dateFormatted = formatEtaForShippingLabel(params.bookingEtaIso);
  const safeRef = params.bookingReference.replace(/[^\w.-]+/g, "_");
  const blob = await pdf(
    <CustomerEtaOverrideShippingLabelDocument
      dateFormatted={dateFormatted}
      reference={params.bookingReference}
    />
  ).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `afwijkende-afleverdatum-${safeRef}.pdf`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
